import { CombinationFilter } from 'components/common/combination-filter-controls';

// Where a shared ducted filter value came from (AHA-6796 EE rows): 'seed' =
// derived from the existing equipment (the context reset is free to re-seed
// it), 'manual' = a user gesture on either surface (the Existing Equipment
// row or the Choose Systems control) — the context reset then PRESERVES it
// instead of re-seeding, because on the Existing Equipment page the results
// context churns on every want/tonnage/component edit and a re-seed there
// would visibly resurrect a selection the user just cleared.
export type DuctedFilterSelectionSource = 'seed' | 'manual';

// The seeded-enum filter composition shared by every equipment-derived spec
// seed (cabinetChassisSize, orientation): the carried spec filters minus
// any entry for `field`, plus the seeded entry when values exist. Shared by
// the resetDuctedSystemTypeCodes reducer and the pre-warm default-params
// builder (buildDefaultDuctedFetchParams) so the two cannot diverge — the
// pre-warm's request hash must be byte-identical to the page's first
// post-reset fetch. Every OTHER spec filter is deliberately carried across
// the reset (the ducted-fetch-params contract); the seeded entry alone is
// context-reset, exactly like fuelSelections — an empty seed strips a stale
// seeded entry from a prior context.
export function withSeededEnumFilter(
  filters: CombinationFilter[],
  field: string,
  seedValues: string[]
): CombinationFilter[] {
  return [
    ...filters.filter((filter) => filter.field !== field),
    ...(seedValues.length > 0
      ? [{ field, kind: 'enum' as const, values: seedValues }]
      : [])
  ];
}

// The context-reset composition for a seeded enum filter (AHA-6796 EE
// rows): seed-sourced → the seeded upsert (withSeededEnumFilter, exactly the
// pre-source behavior); manual-sourced → the carried filters untouched — a
// manual selection absent from the new facet already renders as a clearable
// zero-count chip (the selected-but-absent guarantee), so nothing is ever
// invisible. Shared with the pre-warm builder for hash parity.
export function resolveContextSeededEnumFilters(
  filters: CombinationFilter[],
  field: string,
  source: DuctedFilterSelectionSource,
  seedValues: string[]
): CombinationFilter[] {
  return source === 'manual'
    ? filters
    : withSeededEnumFilter(filters, field, seedValues);
}

// The active enum values for `field` inside a spec-filter list; null = no
// entry. Used by setDuctedCombinationFilters' manual-detection (one rule
// covers both the EE row and the CS facet control).
export function enumFilterValuesOf(
  filters: CombinationFilter[],
  field: string
): string[] | null {
  const found = filters.find(
    (filter) => filter.field === field && filter.kind === 'enum'
  );
  return found && found.kind === 'enum' ? found.values : null;
}

// Content (not order) comparison: a reordered-but-equal entry is not a user
// statement about that field.
export function enumFilterValuesChanged(
  previous: string[] | null,
  next: string[] | null
): boolean {
  if (previous === null || next === null) return previous !== next;
  if (previous.length !== next.length) return true;
  const nextSet = new Set(next);
  return previous.some((value) => !nextSet.has(value));
}
